/**
 * Legend + active-fault list for the diagnostic overlays.
 *
 * A legend is not optional here: both overlays encode meaning in colour, so
 * without it the 3D view is decorative. It is always rendered while an overlay
 * is active.
 */

import React from 'react';
import {
  densityLegend, statusFor, STATUS_ORDER, FOCUS_COLOR, OVERLAY_MUTED, coverageLegend,
  ANSWER_ROLES, ANSWER_ROLE_ORDER, answerRole, STATUS,
} from './diagnosticPalette';
import { agentPartColor } from './overlay.js';
import './DiagnosticLegend.css';

function DensityLegend({ maxCount, error }) {
  const rows = densityLegend();
  return (
    <div className="diaglegend">
      <div className="diaglegend__title">Recorded failure cases</div>
      {error ? (
        <p className="diaglegend__empty">
          Knowledge-graph service unavailable — density unavailable. The model
          and its hierarchy still work.
        </p>
      ) : (
        <>
          <ul className="diaglegend__scale">
            {rows.map((r) => (
              <li className="diaglegend__row" key={r.label} title={r.hint}>
                <span
                  className="diaglegend__swatch"
                  style={{ background: r.color }}
                  aria-hidden="true"
                />
                <span className="diaglegend__label">{r.label}</span>
              </li>
            ))}
          </ul>
          <p className="diaglegend__foot">
            Counts are failure cases linked to each part in the knowledge graph
            {maxCount ? ` (highest: ${maxCount})` : ''}. They describe the
            documented fleet history, not this vehicle&rsquo;s condition.
          </p>
        </>
      )}
    </div>
  );
}

function DtcLegend({ title, activeDtcs, unmapped, onSelect, selectedId, dtcByNode, onTrace }) {
  const present = STATUS_ORDER.filter((key) =>
    activeDtcs.some((a) => String(a.severity).toLowerCase() === key),
  );

  // Which node each code points at, for the click-to-locate affordance.
  const nodeForCode = new Map();
  for (const [nodeId, entry] of dtcByNode.entries()) {
    for (const code of entry.codes) {
      if (!nodeForCode.has(code)) nodeForCode.set(code, nodeId);
    }
  }

  return (
    <div className="diaglegend">
      <div className="diaglegend__title">{title}</div>

      {present.length > 0 && (
        <ul className="diaglegend__scale diaglegend__scale--status">
          {present.map((key) => {
            const s = statusFor(key);
            return (
              <li className="diaglegend__row" key={key}>
                {/* icon + label: the required secondary encoding, so severity
                    never rests on hue alone */}
                <span className="diaglegend__icon" style={{ color: s.color }} aria-hidden="true">
                  {s.icon}
                </span>
                <span className="diaglegend__label">{s.label}</span>
              </li>
            );
          })}
        </ul>
      )}

      {activeDtcs.length === 0 ? (
        <p className="diaglegend__empty">No active fault codes.</p>
      ) : (
        <ul className="diaglegend__codes">
          {activeDtcs.map((a) => {
            const s = statusFor(a.severity);
            const nodeId = nodeForCode.get(a.id) || null;
            const isSelected = nodeId && nodeId === selectedId;
            // Part-knowledge assets trace every message, mapped or not: the
            // trace endpoint knows suspects the local table does not.
            const traceable = typeof onTrace === 'function';
            return (
              <li key={a.id}>
                <button
                  type="button"
                  className={`diaglegend__code${isSelected ? ' is-selected' : ''}`}
                  onClick={() => {
                    if (traceable) onTrace(a.id);
                    else if (nodeId) onSelect?.(nodeId);
                  }}
                  disabled={!nodeId && !traceable}
                  aria-label={traceable ? `Trace ${a.id}: ${a.title}` : undefined}
                  title={traceable ? `Trace ${a.id} — ranked suspects and connection paths`
                    : (nodeId ? `Locate ${a.id} on the model` : 'Not mapped to a part')}
                >
                  <span className="diaglegend__icon" style={{ color: s.color }} aria-hidden="true">
                    {s.icon}
                  </span>
                  <span className="diaglegend__code-id">{a.id}</span>
                  <span className="diaglegend__code-title">{a.title}</span>
                  <span className="diaglegend__code-sev">{traceable ? `${s.label} · Trace ›` : s.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {unmapped.length > 0 && (
        <p className="diaglegend__foot diaglegend__foot--warn">
          {unmapped.length} active code{unmapped.length === 1 ? '' : 's'} not
          mapped to a part ({unmapped.map((u) => u.id).join(', ')}) — shown here
          but not highlighted on the model.
        </p>
      )}
    </div>
  );
}

function CoverageLegend({ error, loading, mean, definition }) {
  return (
    <div className="diaglegend">
      <div className="diaglegend__title">Knowledge coverage</div>
      {error ? (
        <p className="diaglegend__empty">
          Knowledge service unavailable — coverage unavailable. The model and its
          hierarchy still work.
        </p>
      ) : (
        <>
          <ul className="diaglegend__scale">
            {coverageLegend().map((r) => (
              <li className="diaglegend__row" key={r.label}>
                <span className="diaglegend__swatch" style={{ background: r.color }} aria-hidden="true" />
                <span className="diaglegend__label">{r.label}</span>
              </li>
            ))}
          </ul>
          <p className="diaglegend__foot">
            {loading ? 'Loading coverage…' : (definition
              || 'Share of expected specs, failure modes and tasks documented per part.')}
            {!loading && ' It measures the knowledge pack, not the part’s condition.'}
          </p>
          {!loading && Number.isFinite(mean) && (
            <p className="diaglegend__foot">Mean across parts: {Math.round(mean * 100)}%</p>
          )}
        </>
      )}
    </div>
  );
}

function FocusLegend({ count, label }) {
  return (
    <div className="diaglegend">
      <div className="diaglegend__title">From the last answer</div>
      <ul className="diaglegend__scale">
        <li className="diaglegend__row">
          <span className="diaglegend__swatch" style={{ background: FOCUS_COLOR }} aria-hidden="true" />
          <span className="diaglegend__label">
            {count} part{count === 1 ? '' : 's'} the answer reasoned over
          </span>
        </li>
        <li className="diaglegend__row">
          <span className="diaglegend__swatch" style={{ background: OVERLAY_MUTED }} aria-hidden="true" />
          <span className="diaglegend__label">Not referenced</span>
        </li>
      </ul>
      {label ? <p className="diaglegend__foot">&ldquo;{label}&rdquo;</p> : null}
      <p className="diaglegend__foot">
        Matched from the answer&rsquo;s knowledge-graph reasoning path by name.
        An approximate pointer, not a claim that these are the only relevant parts.
      </p>
    </div>
  );
}

/** Health colours the agent's view_highlight uses (CONTRACT §4.3.2), back to status rows. */
const HEALTH_ROWS = [
  { color: '#35A877', icon: '●', label: 'Good' },
  { color: STATUS.warning.color, icon: STATUS.warning.icon, label: 'Warning' },
  { color: STATUS.critical.color, icon: STATUS.critical.icon, label: 'Critical' },
];

/**
 * The agent's highlight: rank rows when it ranked suspects, health rows when it coloured by health,
 * otherwise role rows (icon + label, ANSWER_ROLES). Part names are buttons that select the part.
 */
function AgentLegend({ highlight, tree, onSelect, selectedId }) {
  const ids = (highlight?.ids ?? highlight?.part_ids ?? []).filter((id) => !tree?.byId || tree.byId.has(id));
  const nameOf = (id) => tree?.byId?.get(id)?.name ?? id;
  const ranks = highlight?.ranks || null;
  const colors = highlight?.colors || null;
  const hasRanks = ranks && ids.some((id) => ranks[id]);
  const hasColors = colors && ids.some((id) => colors[id]);

  const partButton = (id) => (
    <button
      key={id}
      type="button"
      className={`diaglegend__code diaglegend__code--agent${id === selectedId ? ' is-selected' : ''}`}
      onClick={() => onSelect?.(id)}
      title={id}
    >
      <span className="diaglegend__swatch" style={{ background: agentPartColor(highlight, id) }} aria-hidden="true" />
      <span className="diaglegend__code-title">{nameOf(id)}</span>
      {hasRanks && ranks[id] ? <span className="diaglegend__code-sev">{`#${ranks[id]}`}</span> : null}
    </button>
  );

  let rows;
  if (hasRanks) {
    const sorted = [...ids].sort((a, b) => (ranks[a] ?? 99) - (ranks[b] ?? 99));
    rows = (
      <ul className="diaglegend__codes">
        {sorted.map((id) => (
          <li key={id}>
            {partButton(id)}
          </li>
        ))}
      </ul>
    );
  } else if (hasColors) {
    const present = HEALTH_ROWS.filter((r) => ids.some((id) => String(colors[id] || '').toLowerCase() === r.color.toLowerCase()));
    rows = (
      <>
        <ul className="diaglegend__scale diaglegend__scale--status">
          {present.map((r) => (
            <li className="diaglegend__row" key={r.label}>
              <span className="diaglegend__icon" style={{ color: r.color }} aria-hidden="true">{r.icon}</span>
              <span className="diaglegend__label">{r.label}</span>
            </li>
          ))}
        </ul>
        <ul className="diaglegend__codes">{ids.map((id) => <li key={id}>{partButton(id)}</li>)}</ul>
      </>
    );
  } else {
    const roles = highlight?.roles || {};
    const byRole = ANSWER_ROLE_ORDER
      .map((key) => ({ key, ids: ids.filter((id) => answerRole(roles[id]) === ANSWER_ROLES[key]) }))
      .filter((g) => g.ids.length > 0);
    rows = (
      <ul className="diaglegend__codes">
        {byRole.map((g) => {
          const role = ANSWER_ROLES[g.key];
          return (
            <li key={g.key} className="diaglegend__group">
              <div className="diaglegend__row">
                <span className="diaglegend__icon" style={{ color: role.color }} aria-hidden="true">{role.icon}</span>
                <span className="diaglegend__label">{role.label}</span>
              </div>
              {g.ids.map((id) => partButton(id))}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="diaglegend">
      <div className="diaglegend__title">From the agent</div>
      {rows}
      <ul className="diaglegend__scale">
        <li className="diaglegend__row">
          <span className="diaglegend__swatch" style={{ background: OVERLAY_MUTED }} aria-hidden="true" />
          <span className="diaglegend__label">Not highlighted</span>
        </li>
      </ul>
      {highlight?.label ? <p className="diaglegend__foot">&ldquo;{highlight.label}&rdquo;</p> : null}
      {hasRanks && (
        <p className="diaglegend__foot">
          Rank 1 is the strongest suspect; brighter violet means a stronger suspicion.
        </p>
      )}
    </div>
  );
}

export default function DiagnosticLegend({
  mode, maxCount, densityError, activeDtcs, unmapped, dtcByNode, onSelect,
  selectedId, focusCount = 0, focusLabel = '', dtcTitle = 'Active fault codes',
  onTrace = null, coverageError = null, coverageLoading = false,
  coverageMean = null, coverageDefinition = '',
  agentHighlight = null, tree = null,
}) {
  if (mode === 'agent') {
    return <AgentLegend highlight={agentHighlight} tree={tree} onSelect={onSelect} selectedId={selectedId} />;
  }
  if (mode === 'coverage') {
    return (
      <CoverageLegend
        error={coverageError}
        loading={coverageLoading}
        mean={coverageMean}
        definition={coverageDefinition}
      />
    );
  }
  if (mode === 'focus') {
    return <FocusLegend count={focusCount} label={focusLabel} />;
  }
  if (mode === 'density') {
    return <DensityLegend maxCount={maxCount} error={densityError} />;
  }
  if (mode === 'dtc') {
    return (
      <DtcLegend
        title={dtcTitle}
        activeDtcs={activeDtcs}
        unmapped={unmapped}
        dtcByNode={dtcByNode}
        onSelect={onSelect}
        selectedId={selectedId}
        onTrace={onTrace}
      />
    );
  }
  return null;
}
